import React from 'react';
import { ArrowUpRight, Github } from 'lucide-react';
import { Link } from 'react-router-dom';
import LoretoGlyph from './LoretoGlyph';
import X402Badge from './X402Badge';
import { FOOTER_LINKS, LINKS } from '../data/content';

const isExternal = (href) => /^https?:/i.test(href);

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="relative border-t border-[var(--border)] bg-[var(--bg)]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="grid grid-cols-12 gap-y-10 md:gap-10 mb-20">
          <div className="col-span-12 lg:col-span-4">
            <div className="flex items-center gap-2.5 mb-7">
              <LoretoGlyph size={30} className="shrink-0" />
              <span className="serif text-[1.7rem] leading-none tracking-tight">Loreto</span>
            </div>
            <p className="serif text-[2.4rem] md:text-[3.2rem] leading-[1.05] tracking-[-0.02em] max-w-[18ch]">
              The skills runtime for Claude Code.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to={LINKS.keys} className="btn-primary !py-2.5 !px-5 !text-[0.88rem]">
                Get your keys <ArrowUpRight size={14} />
              </Link>
              <a href={LINKS.github} target="_blank" rel="noreferrer" className="btn-ghost !py-2.5 !px-5 !text-[0.88rem]">
                <Github size={14} /> Repo
              </a>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-10 gap-x-8">
              {Object.entries(FOOTER_LINKS).map(([col, links]) => (
                <div key={col}>
                  <div className="eyebrow mb-6">{col}</div>
              <ul className="space-y-3.5">
                {links.map((l) => {
                  const cls =
                    'group inline-flex items-center gap-1.5 text-[var(--text-2)] hover:text-[var(--text)] transition-colors text-[0.95rem]';
                  const arrow = (
                    <ArrowUpRight
                      size={13}
                      className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                    />
                  );
                  return (
                    <li key={l.label}>
                      {isExternal(l.href) ? (
                        <a href={l.href} target="_blank" rel="noreferrer" className={cls}>
                          {l.label}
                          {arrow}
                        </a>
                      ) : l.native ? (
                        // Server-rendered static SEO page — full navigation, not client routing.
                        <a href={l.href} className={cls}>
                          {l.label}
                          {arrow}
                        </a>
                      ) : (
                        <Link to={l.href} className={cls}>
                          {l.label}
                          {arrow}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
              ))}
            </div>
          </div>
        </div>

        {/* Our own badge first, then the two we were given. Ours is drawn in
            the page and points at the docs; theirs are remote images pointing
            back at their listings. Sits above the hairline so the bottom bar
            keeps its two-column justify-between layout. */}
        <div className="flex flex-wrap items-center gap-4 justify-start md:justify-end mb-8">
          <X402Badge />
          <a
            href="https://codehype.ai/product/loreto?utm_source=codehype_badge"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Featured on CodeHype"
            className="inline-block opacity-70 hover:opacity-100 transition-opacity"
          >
            <img
              src="https://codehype.ai/badges/loreto.svg?variant=find-us&v=20"
              alt="Featured on CodeHype"
              width={180}
              height={65}
              loading="lazy"
              decoding="async"
              style={{
                display: 'inline-block',
                border: 0,
                width: '100%',
                maxWidth: 128,
                height: 'auto',
                maxHeight: 46,
              }}
            />
          </a>
          <a
            href="https://www.producthunt.com/products/loreto?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-loreto"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Loreto on Product Hunt"
            className="inline-block opacity-70 hover:opacity-100 transition-opacity"
          >
            <img
              src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1254178&theme=light&t=1789702534348"
              alt="Loreto - Turn any repo, doc, or video into an AI agent skill | Product Hunt"
              width={250}
              height={54}
              loading="lazy"
              decoding="async"
              style={{
                display: 'inline-block',
                border: 0,
                width: '100%',
                maxWidth: 178,
                height: 'auto',
                maxHeight: 38,
              }}
            />
          </a>
        </div>

        <hr className="hairline" />

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mt-8">
          <div className="mono text-[0.72rem] tracking-[0.18em] uppercase text-[var(--text-3)]">
            © {year} Loreto — Crafted for agents that compound.
          </div>
          <div className="mono text-[0.72rem] tracking-[0.18em] uppercase text-[var(--text-4)] flex items-center gap-4">
            <span>MCP / Claude Code</span>
            <span className="w-1 h-1 rounded-full bg-[var(--border-2)]" />
            <span>v1.0</span>
            <span className="w-1 h-1 rounded-full bg-[var(--border-2)]" />
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="hover:text-[var(--accent)] transition-colors uppercase tracking-[0.18em]"
            >
              Back to top ↑
            </button>
          </div>
        </div>

        {/* Giant footer wordmark */}
        <div className="relative mt-16 overflow-hidden">
          <div
            aria-hidden
            className="serif select-none text-[28vw] md:text-[20vw] leading-none tracking-[-0.05em] text-transparent"
            style={{
              WebkitTextStroke: '1px var(--border-2)',
            }}
          >
            Loreto.
          </div>
        </div>
      </div>
    </footer>
  );
}
